<template>
  <div class="sensor-dialog">
    <el-dialog
      width="90%"
      top="0"
      :model-value="isShowDialog"
      :close-on-click-modal="true"
      :show-close="true"
      @closed="$emit('dialog:close')"
      :title="$t('sensor.sensor_info')"
    >
      <div class="dialogMessage w-full">
        <div class="flex flex-col items-center">
          <div class="text-center px-4 py-4 truncate w-44 rounded-full bg-gray-400 text-white mt-4">
            {{ previousNodeName }}
          </div>
          <div class="w-fit">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" viewBox="0 0 20 58">
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="0"
                  refY="1.75"
                  orient="auto"
                  fill="#9CA3AF"
                >
                  <polygon points="0 0, 5 1.75, 0 3.5" />
                </marker>
              </defs>
              <line x1="10" y1="0" x2="10" y2="38" stroke="#9CA3AF" stroke-width="4" marker-end="url(#arrowhead)" />
            </svg>
          </div>
          <div class="text-center px-4 py-4 truncate w-44 rounded-full bg-white bg-blue-600 text-white mb-8">
            {{ currentNode.name }}
          </div>
        </div>
        <div>
          <el-tabs type="card">
            <el-tab-pane :label="$t('sensor.temperature')">
              <div id="temperatureChartContainer" ref="temperatureChartContainer"></div>
            </el-tab-pane>
            <el-tab-pane :label="$t('sensor.humidity')">
              <div id="humidityChartContainer" ref="humidityChartContainer"></div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-center">
          <el-button type="primary" round class="ok-btn" @click="$emit('dialog:close')">
            {{ $t('OK') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>
<script lang="ts">
import { IProject, IBarcode, ITrackingData, ISensor } from '@/utils/types'
import * as am4charts from '@amcharts/amcharts4/charts'
import * as am4core from '@amcharts/amcharts4/core'
import m4themesAnimated from '@amcharts/amcharts4/themes/animated'
import { Options, Vue } from 'vue-class-component'
import { Prop, Ref, Watch } from 'vue-property-decorator'
am4core.useTheme(m4themesAnimated)
am4core.options.autoDispose = true

@Options({
  components: {},
  emits: ['dialog:close'],
})
export default class SensorDialog extends Vue {
  @Prop({ type: Object }) readonly project!: IProject
  @Prop({ type: Object }) readonly barcode!: IBarcode
  @Prop({ type: Boolean }) readonly isShowDialog!: boolean
  @Prop({ type: Number }) readonly trackingDataIndex!: number
  @Ref() temperatureChartContainer!: HTMLElement
  @Ref() humidityChartContainer!: HTMLElement

  @Watch('isShowDialog')
  sensorChanged() {
    if (!this.isShowDialog) {
      this.temperatureChartContainer.innerHTML = ''
      this.humidityChartContainer.innerHTML = ''
      return
    }
    this.$nextTick(() => {
      this.createTemperatureChart()
      this.createHumidityChart()
    })
  }

  createTemperatureChart() {
    const chart = am4core.create(this.temperatureChartContainer, am4charts.XYChart)
    chart.data = this.temperatureFormatData

    // Create axes
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.renderer.minGridDistance = 60

    chart.yAxes.push(new am4charts.ValueAxis())

    // Create series
    const series = chart.series.push(new am4charts.LineSeries())
    series.dataFields.valueY = 'value'
    series.dataFields.dateX = 'date'
    series.tooltipText = '{value}'
    series.strokeWidth = 1
    // series.tensionX = 0.8
    series.bullets.push(new am4charts.CircleBullet())

    // series.tooltip.pointerOrientation = "vertical";

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.snapToSeries = series
    chart.cursor.xAxis = dateAxis

    chart.scrollbarX = new am4core.Scrollbar()
  }

  createHumidityChart() {
    const chart = am4core.create(this.humidityChartContainer, am4charts.XYChart)
    chart.data = this.humidityFormatData

    // Create axes
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.renderer.minGridDistance = 60

    chart.yAxes.push(new am4charts.ValueAxis())

    // Create series
    const series = chart.series.push(new am4charts.LineSeries())
    series.dataFields.valueY = 'value'
    series.dataFields.dateX = 'date'
    series.tooltipText = '{value}'
    series.strokeWidth = 1
    // series.tensionX = 0.8
    series.bullets.push(new am4charts.CircleBullet())

    // series.tooltip.pointerOrientation = "vertical";

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.snapToSeries = series
    chart.cursor.xAxis = dateAxis

    chart.scrollbarX = new am4core.Scrollbar()
  }

  get currentTrackingData(): ITrackingData {
    return this.barcode.trackingData[this.trackingDataIndex]
  }

  get sensorData(): ISensor[] {
    return this.currentTrackingData.sensorData!
  }

  get temperatureFormatData() {
    return this.sensorData.map((sensor) => ({ date: sensor.timeOfReport, value: sensor.temperatureC }))
  }

  get humidityFormatData() {
    return this.sensorData.map((sensor) => ({ date: sensor.timeOfReport, value: sensor.humidity }))
  }

  get previousNodeName(): string | undefined {
    return this.project.trackPoints[this.currentTrackingData.currentTrackPointKey].name
  }

  get currentNode() {
    return this.project.trackPoints[this.currentTrackingData.nextTrackPointKey]
  }
}
</script>
<style lang="scss" scoped>
.reconcile-btn {
  min-width: 120px;
  background-color: #c4c4c4;
  color: #ffffff;

  &.is-round {
    height: 64px;
    border-radius: 40px !important;
    box-sizing: border-box !important;
  }
}

.ok-btn {
  width: 64px;
  height: 64px;
  background: #525252;
  color: #ffffff;

  &.is-round {
    height: 64px;
    border-radius: 40px !important;
    box-sizing: border-box !important;
  }
}

.sensor-dialog :deep() {
  .el-tabs {
    .el-tabs__nav-scroll {
      display: flex;
      justify-content: center;
    }
    .el-tabs__header {
      margin: 0;
    }

    .el-tabs__nav {
      border: 0px solid #e4e7ed;
    }

    .el-tabs__item {
      padding: 0 20px !important;
      border: 1px solid #c4c4c4;
      border-radius: 4px 4px 0 0;
      margin: 0 10px 1px 0;
      border-bottom: none;

      &.is-active {
        background-color: #525252;
        border-color: #000;
        color: #fff;
      }
    }

    .el-tabs__active-bar {
      background: none;
    }

    .el-tabs__content {
      padding: 15px 0 !important;
    }

    .el-tabs__nav-wrap::after {
      height: 1px;
      background-color: #525252;
    }
  }
}

#temperatureChartContainer {
  width: 100%;
  height: 300px;
}

#humidityChartContainer {
  width: 100%;
  height: 300px;
}
</style>
